import { Injectable, Injector } from '@angular/core';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { ITask2ReportAvailableItems, ITask2ReportWidgetFilter } from '../fi-task-v2-report';
import { ILightWeightObject } from '@fi-shared/models';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { FiTaskCategories, FiTaskPriorities } from '@fi-app/task-v2/fi-task-v2-consts';
import { FiTaskReportType } from '@fi-app/task-v2/fi-task-v2-report/fi-task-v2-report-enums';
import { FiReportTableFilterHelperBase } from '@fi-shared/fi-report-table-filter/fi-report-table-filter-helper-base';
import { cloneDeep, isEqual } from '@fi-app/shared/utils';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskStatuses } from '@fi-app/task-v2/fi-task-v2-consts';

interface IFilterMenuCreateArgs {
    widgetReportType: FiTaskReportType;
}

interface ITaskMenuItem {
    Id: string;
    Name: string;
}

@Injectable()
export class FiTaskV2ReportTableFilterHelperService extends FiReportTableFilterHelperBase {
    constructor(injector: Injector) {
        super(injector);
    }

    getFilterMenusData(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdObj>[] {
        const menusData = [];
        menusData.push(this.getTasksMenu());
        menusData.push(this.getTaskStatusesMenu(args.widgetReportType));
        menusData.push(this.getTaskPrioritiesMenu());
        menusData.push(this.getTaskCategoriesMenu(args.widgetReportType));
        menusData.push(this.getRolesMenu(args.widgetReportType));
        menusData.push(
            this.getBusinessUnitsMenu({
                isAvailable: args.widgetReportType !== FiTaskReportType.Private,
            }),
        );
        menusData.push(
            this.getUsersMenu({
                isAvailable: args.widgetReportType !== FiTaskReportType.Private,
            }),
        );
        return menusData;
    }

    private getTasksMenu(): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get('task.tasks'),
            menuItems: [],
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.TaskName,
            selectedItemIds: [],
        };
    }

    private getEnumForTests(_enum: any): INameIdObj[] {
        // todo
        return Object.keys(_enum)
            .filter((key) => !isNaN(Number(key)))
            .map((key) => {
                return {
                    Id: Number(key),
                    Name: _enum[key],
                };
            });
    }

    private getTaskStatusesMenu(widgetReportType: FiTaskReportType): ISharingMenuData<ILightWeightObject> {
        const availableStatuses = FiTaskStatuses.filter((item) => {
            if (item.id === FiTaskStatusType.Cancelled) {
                // hide cancelled status for all reports
                return false;
            }
            if (widgetReportType === FiTaskReportType.Overdue && item.id === FiTaskStatusType.Completed) {
                // hide completed status for overdue reports
                return false;
            }
            return true;
        });
        return {
            label: this.appTextService.get('common.status'),
            menuItems: availableStatuses.map((item) => ({
                Id: item.id,
                Name: this.appTextService.get(item.textId),
            })),
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.TaskStatus,
            selectedItemIds: [],
            isSearchByNameInputHidden: true,
        };
    }

    private getTaskPrioritiesMenu(): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get('common.priority'),
            menuItems: FiTaskPriorities.map((item) => ({ Id: item.id, Name: this.appTextService.get(item.textId) })),
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.TaskPriority,
            selectedItemIds: [],
            isSearchByNameInputHidden: true,
        };
    }

    private getTaskCategoriesMenu(widgetReportType: FiTaskReportType): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get('common.category'),
            menuItems: FiTaskCategories.map((item) => ({ Id: item.id, Name: this.appTextService.get(item.textId) })),
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.TaskCategory,
            selectedItemIds: [],
            isSearchByNameInputHidden: true,
            isAvailable: () =>
                ![FiTaskReportType.AssignedToMe, FiTaskReportType.AssignedByMe, FiTaskReportType.Private].includes(
                    widgetReportType,
                ),
        };
    }

    private getRolesMenu(widgetReportType: FiTaskReportType): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get('common.roles'),
            menuItems: [],
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.Role,
            selectedItemIds: [],
            isAvailable: () => widgetReportType !== FiTaskReportType.Private,
        };
    }

    getSelectedFilterItemsFromFilter(
        currentFilter: ITask2ReportWidgetFilter,
        availableItems: ITask2ReportAvailableItems,
    ): ISelectedFilterItem[] {
        const selectedTasks = this.getSelectedFilterItemsFromStrings(currentFilter.TaskNames, FiFilterType.TaskName);
        const selectedStatuses = this.getSelectedFilterItemsFromEnum(
            currentFilter.TaskStatuses,
            FiTaskStatuses,
            FiFilterType.TaskStatus,
        );
        const selectedPriorities = this.getSelectedFilterItemsFromEnum(
            currentFilter.TaskPriorities,
            FiTaskPriorities,
            FiFilterType.TaskPriority,
        );
        const selectedCategories = this.getSelectedFilterItemsFromEnum(
            currentFilter.TaskCategories,
            FiTaskCategories,
            FiFilterType.TaskCategory,
        );
        const selectedRoles = this.getSelectedFilterItemsFromAvailableItems(
            currentFilter.RoleIds,
            availableItems.Roles,
            FiFilterType.Role,
        );
        const selectedBusinessUnits = this.getSelectedFilterItemsFromAvailableItems(
            currentFilter.BusinessUnitIds,
            availableItems.BusinessUnits,
            FiFilterType.BusinessUnit,
        );
        const selectedUsers = this.getSelectedFilterItemsFromAvailableUsers(
            currentFilter.UserIds,
            availableItems.Users,
        );
        return [
            ...selectedTasks,
            ...selectedStatuses,
            ...selectedPriorities,
            ...selectedCategories,
            ...selectedRoles,
            ...selectedBusinessUnits,
            ...selectedUsers,
        ];
    }

    getFilterFromSelectedItems(
        selectedFilterItemGroups: { [key: number]: ISelectedFilterItem[] },
        filter: ITask2ReportWidgetFilter,
    ) {
        const newFilter: ITask2ReportWidgetFilter = {
            ...filter,
            BusinessUnitIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.BusinessUnit]),
            RoleIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.Role]),
            TaskNames: this.getValuesStrFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.TaskName]),
            TaskCategories: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.TaskCategory]),
            TaskPriorities: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.TaskPriority]),
            TaskStatuses: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.TaskStatus]),
            UserIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.User]),
        };
        Object.keys(newFilter).forEach((key) => {
            if (!newFilter[key as keyof ITask2ReportWidgetFilter]?.length) {
                delete newFilter[key as keyof ITask2ReportWidgetFilter];
            }
        });

        return newFilter;
    }

    updateMenusData(
        menusData: ISharingMenuData<any>[],
        availableItems: ITask2ReportAvailableItems,
        firstChange: boolean,
    ) {
        if (firstChange) {
            // update units/roles/users only once
            this.updateRoleItems(menusData, availableItems.Roles);
            this.updateBusinessUnitItems(menusData, availableItems.BusinessUnits);
            this.updateUserItems(menusData, availableItems.Users);
        }

        this.updateTaskItems(menusData, availableItems.TaskNames);
    }

    private updateRoleItems(menusData: ISharingMenuData<INameIdObj>[], roles: ILightWeightObject[]) {
        this.getMenusDataItem(menusData, FiFilterType.Role).menuItems = roles.map((item) => ({
            Id: item.Id,
            Name: item.Name,
        }));
    }
    private updateTaskItems(menusData: ISharingMenuData<ITaskMenuItem>[], taskNames: string[]) {
        this.getMenusDataItem(menusData, FiFilterType.TaskName).menuItems = taskNames.map((taskName) => ({
            Id: taskName,
            Name: taskName,
        }));
    }

    isFilterEqualsToInitial(filter: ITask2ReportWidgetFilter, initialFilter: ITask2ReportWidgetFilter): boolean {
        const initialFilterCopy: ITask2ReportWidgetFilter = cloneDeep(initialFilter);
        delete initialFilterCopy.Name;
        delete initialFilterCopy.Guid;
        const currentFilterCopy: ITask2ReportWidgetFilter = cloneDeep(filter);
        delete currentFilterCopy.Name;
        delete currentFilterCopy.Guid;
        return !isEqual(currentFilterCopy, initialFilterCopy);
    }

    isFilterEmpty(filter: ITask2ReportWidgetFilter): boolean {
        return (
            !filter.TaskNames?.length &&
            !filter.TaskStatuses?.length &&
            !filter.TaskPriorities?.length &&
            !filter.TaskCategories?.length &&
            !filter.RoleIds?.length &&
            !filter.BusinessUnitIds?.length &&
            !filter.UserIds?.length
        );
    }
}
